import React, { useState, useEffect } from 'react';
import { getActiveServices, updateServiceOrder } from '../api/services';
import { getAllHRVProperties, updateHRVOrder } from '../api/hrv';
import '../css/organizeServices.css';

export default function OrganizeServices() {
  const [activeTab, setActiveTab] = useState('stay');
  const [services, setServices] = useState({
    stay: [],
    experience: [],
    event: [],
    hrv: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Helper function to reset all drag states
  const resetDragState = () => {
    setDraggedItem(null);
    setDragOverItem(null);
    
    // Clean up any visual states safely
    try {
      const draggedElements = document.querySelectorAll('.organize-service-item');
      draggedElements.forEach(el => {
        if (el && el.style) {
          el.style.opacity = '';
        }
      });
    } catch (error) {
      console.warn('Error cleaning up drag visual states:', error);
    }
  };

  // Fetch services on component mount
  useEffect(() => {
    fetchServices();
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Reset drag states if component unmounts during drag
      resetDragState();
    };
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      
      // Fetch services
      const servicesResponse = await getActiveServices();
      
      // Fetch HRV properties
      const hrvResponse = await getAllHRVProperties();
      
      if (servicesResponse.success && servicesResponse.data && hrvResponse.success && hrvResponse.data) {
        const categorizedServices = {
          stay: servicesResponse.data
            .filter(service => service.category === 'stay')
            .sort((a, b) => (a.order || 0) - (b.order || 0)),
          experience: servicesResponse.data
            .filter(service => service.category === 'experience')
            .sort((a, b) => (a.order || 0) - (b.order || 0)),
          event: servicesResponse.data
            .filter(service => service.category === 'event')
            .sort((a, b) => (a.order || 0) - (b.order || 0)),
          hrv: hrvResponse.data
            .sort((a, b) => (a.order || 0) - (b.order || 0))
        };
        
        setServices(categorizedServices);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers - Robust implementation
  const handleDragStart = (e, index) => {
    console.log(`Starting drag for item at position ${index + 1}`);
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    
    // Store the dragged item data for cross-browser compatibility
    e.dataTransfer.setData('text/plain', index.toString());
    
    // Add visual feedback immediately
    setTimeout(() => {
      const draggedElement = document.querySelector('.organize-service-item.dragging');
      if (draggedElement) {
        draggedElement.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Only show drop indicator if we're dragging and not over the same item
    if (draggedItem !== null && draggedItem !== index) {
      setDragOverItem(index);
    }
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    
    // Clear any existing timeouts for drag leave
    if (draggedItem !== null && draggedItem !== index) {
      setDragOverItem(index);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    
    // Use a small timeout to prevent flickering when moving between child elements
    setTimeout(() => {
      // Check if the element still exists and is mounted
      if (!e.currentTarget || typeof e.currentTarget.getBoundingClientRect !== 'function') {
        setDragOverItem(null);
        return;
      }
      
      try {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        
        // Check if the mouse is actually outside the element
        if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
          setDragOverItem(null);
        }
      } catch (error) {
        // Element was removed from DOM, clear the drag over state
        console.warn('Element removed during drag operation:', error);
        setDragOverItem(null);
      }
    }, 10);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`Drop event at position ${dropIndex + 1}`);
    
    // Validate drag state
    if (draggedItem === null || draggedItem === undefined) {
      console.warn('No dragged item found');
      resetDragState();
      return;
    }

    // Don't do anything if dropping on the same position
    if (draggedItem === dropIndex) {
      console.log('Dropped on same position, no changes needed');
      resetDragState();
      return;
    }

    const currentServices = services[activeTab];
    
    // Validate indices
    if (draggedItem < 0 || draggedItem >= currentServices.length || 
        dropIndex < 0 || dropIndex >= currentServices.length) {
      console.error('Invalid drag indices:', { draggedItem, dropIndex, serviceCount: currentServices.length });
      resetDragState();
      return;
    }

    // Create a deep copy to avoid mutations
    const newServices = currentServices.map(service => ({ ...service }));
    
    // Perform the swap/reorder operation
    const draggedService = newServices[draggedItem];
    
    // Remove the dragged item first
    newServices.splice(draggedItem, 1);
    
    // Insert at the target position
    newServices.splice(dropIndex, 0, draggedService);
    
    // Log the operation for debugging
    console.log(`Successfully moved "${draggedService.title}" from position ${draggedItem + 1} to ${dropIndex + 1}`);
    console.log('Before:', currentServices.map((s, i) => `${i + 1}: ${s.title}`));
    console.log('After:', newServices.map((s, i) => `${i + 1}: ${s.title}`));
    
    // Update the state
    setServices(prev => ({
      ...prev,
      [activeTab]: newServices
    }));
    
    setHasChanges(true);
    resetDragState();
  };

  const handleDragEnd = (e) => {
    console.log('Drag operation ended');
    resetDragState();
  };

  // Save changes
  const saveChanges = async () => {
    try {
      if (activeTab === 'hrv') {
        const hrvIds = services[activeTab].map(hrv => hrv._id);
        console.log('Saving HRV order changes...', { hrvIds });
        
        const response = await updateHRVOrder(hrvIds);
        console.log('Save response:', response);
        
        setHasChanges(false);
        alert(`HRV order saved successfully! Updated ${response.data.updated} of ${response.data.total} properties.`);
      } else {
        const serviceIds = services[activeTab].map(service => service._id);
        console.log('Saving service order changes...', { category: activeTab, serviceIds });
        
        const response = await updateServiceOrder(activeTab, serviceIds);
        console.log('Save response:', response);
        
        setHasChanges(false);
        alert(`Service order saved successfully! Updated ${response.data.updated} of ${response.data.total} services.`);
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      alert(`Failed to save changes: ${err.message}. Please try again.`);
    }
  };

  // Reset to original order
  const resetOrder = () => {
    if (window.confirm('Are you sure you want to reset the order? This will discard all unsaved changes.')) {
      fetchServices();
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="organize-services-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="organize-services-container">
        <div className="error-state">
          <i className="fas fa-exclamation-triangle"></i>
          <h3>Error Loading Services</h3>
          <p>{error}</p>
          <button onClick={fetchServices} className="retry-btn">
            <i className="fas fa-redo"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="organize-services-container">
      <div className="organize-header">
        <h2>
          <i className="fas fa-sort"></i>
          Organize Services
        </h2>
        <p>Drag and drop services to reorder them. Changes are permanent once saved.</p>
      </div>

      {/* Tab Navigation */}
      <div className="organize-tabs">
        <button
          className={`tab-button ${activeTab === 'stay' ? 'active' : ''}`}
          onClick={() => setActiveTab('stay')}
        >
          <i className="fas fa-bed"></i>
          Stay ({services.stay.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'experience' ? 'active' : ''}`}
          onClick={() => setActiveTab('experience')}
        >
          <i className="fas fa-mountain"></i>
          Experience ({services.experience.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'event' ? 'active' : ''}`}
          onClick={() => setActiveTab('event')}
        >
          <i className="fas fa-calendar-alt"></i>
          Event ({services.event.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'hrv' ? 'active' : ''}`}
          onClick={() => setActiveTab('hrv')}
        >
          <i className="fas fa-home"></i>
          HRV ({services.hrv.length})
        </button>
      </div>

      {/* Action Buttons */}
      {hasChanges && (
        <div className="organize-actions">
          <button onClick={saveChanges} className="save-btn">
            <i className="fas fa-save"></i>
            Save Order
          </button>
          <button onClick={resetOrder} className="reset-btn">
            <i className="fas fa-undo"></i>
            Reset
          </button>
        </div>
      )}

      {/* Services List */}
      <div className="organize-content">
        <div className="services-list">
          {services[activeTab].length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <h3>No {activeTab} {activeTab === 'hrv' ? 'properties' : 'services'} found</h3>
              <p>Add some {activeTab} {activeTab === 'hrv' ? 'properties' : 'services'} to organize their display order.</p>
            </div>
          ) : (
            services[activeTab].map((item, index) => (
              <div 
                key={item._id} 
                className={`organize-service-item ${draggedItem === index ? 'dragging' : ''} ${dragOverItem === index ? 'drag-over' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <div className="drag-handle" title="Drag to reorder">
                  <i className="fas fa-grip-vertical"></i>
                </div>
                
                <div className="organize-service-info">
                  <div className="organize-service-image">
                    <img 
                      src={activeTab === 'hrv' ? (item.images && item.images[0] || '/placeholder.jpg') : item.image} 
                      alt={item.title} 
                    />
                  </div>
                  <div className="organize-service-details">
                    <h4>{item.title}</h4>
                    {activeTab === 'hrv' ? (
                      <>
                        <p className="organize-service-category">HRV Property</p>
                        <p className="organize-service-description">
                          {item.location} - ${item.price}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="organize-service-category">{item.category}</p>
                        <p className="organize-service-description">
                          {item.description && item.description.length > 100 
                            ? `${item.description.substring(0, 100)}...` 
                            : item.description}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="order-controls">
                  <span className="position-indicator">#{index + 1}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  );
}